import React, { Fragment, useState, useEffect } from "react";
import { useScrollSections } from "react-scroll-section";

import { 
  Typography, 
  Paper, 
  Tabs, 
  Tab, 
  AppBar, 
  Toolbar,  
  Badge, 
  IconButton, 
  InputBase, 
  InputAdornment,
  Box,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import MenuIcon from '@mui/icons-material/Menu';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { NavLink } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { selectedCategory } from "store/actions/menu";

import CallButton from "./CallButton";

import { loadTableId, isDineInTable, decodeRestaurantId } from "util/utils";
import { updateSearch } from 'store/actions/menu';



function a11yProps(index) {
  return {
    id: `scrollable-auto-tab-${index}`,
    "aria-controls": `scrollable-auto-tabpanel-${index}`,
  };
}

// Modern styling using sx prop
const styles = {
  fixedWrapper: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1100,
    width: "100%",
    maxWidth: "100%",
    backgroundColor: "#fff",
  },
  spacer: {
    width: "100%",
  },
  appBar: {
    position: "relative",
    backgroundColor: "primary.main",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
    width: "100%",
    maxWidth: "100%",
    overflowX: "hidden",
  },
  toolbar: {
    minHeight: 64,
    padding: { xs: 1, sm: 2 },
    width: "100%",
    maxWidth: "100%",
    overflowX: "hidden",
  },
  searchContainer: {
    position: "relative",
    borderRadius: 3,
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 1)",
    },
    margin: { xs: 1, sm: 2 },
    width: "100%",
    maxWidth: "100%",
    transition: "all 0.3s ease",
    "&:focus-within": {
      backgroundColor: "white",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    },
    overflowX: "hidden",
  },
  searchIcon: {
    padding: { xs: 1, sm: 2 },
    height: "100%",
    position: "absolute",
    pointerEvents: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "text.secondary",
  },
  searchInput: {
    padding: { xs: 1, sm: 1.5 },
    paddingLeft: { xs: 4, sm: 5 },
    width: "100%",
    fontSize: { xs: "0.9rem", sm: "1rem" },
    "&::placeholder": {
      color: "text.secondary",
      opacity: 0.7,
    },
  },
  tabsContainer: {
    background: "#ffffff",
    borderTop: "1px solid rgba(0, 0, 0, 0.05)",
    width: "100%",
    maxWidth: "100%",
    overflowX: "hidden",
  },
  tab: {
    minHeight: 48,
    textTransform: "none",
    fontWeight: 600,
    fontSize: { xs: "0.8rem", sm: "0.9rem" },
    color: "text.secondary",
    "&.Mui-selected": {
      color: "primary.main",
      fontWeight: 700,
    },
  },
  cartButton: {
    marginLeft: "auto",
    transition: "all 0.3s ease",
    "&:hover": {
      transform: "scale(1.1)",
    },
  },
  cartIcon: {
    width: 28,
    height: 28,
    color: "primary.contrastText",
  },
  tableIcon: {
    width: 32,
    height: 32,
    marginRight: 1,
    color: "primary.contrastText",
  },
  restaurantName: {
    fontWeight: 700,
    fontSize: { xs: "1rem", sm: "1.15rem" },
    color: "primary.contrastText",
    lineHeight: 1.2,
  },
  orderType: {
    fontWeight: 500,
    fontSize: { xs: "0.85rem", sm: "0.95rem" },
    color: "primary.contrastText",
    lineHeight: 1.2,
    marginTop: 0.5,
    opacity: 0.9,
  },
  myOrdersLink: {
    textDecoration: "none",
    color: "primary.contrastText",
    fontWeight: 600,
    fontSize: "0.9rem",
    padding: "8px 16px",
    borderRadius: 2,
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    transition: "all 0.3s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.3)",
      transform: "translateY(-1px)",
    },
  },
  menuButton: {
    color: "primary.contrastText",
    marginRight: 1,
  },
  drawer: {
    width: 250,
  },
  drawerHeader: {
    padding: 2,
    backgroundColor: "primary.main",
    color: "primary.contrastText",
  },
  listItemLink: {
    textDecoration: "none",
    color: "inherit",
    display: "block",
    width: "100%",
  },
};

const DynamicCategories = (props) => {
  const dispatch = useDispatch();
  const { categories, tableIdDisplay, menuId, tableId, ordersSummary, restaurantName } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const sections = useScrollSections();
  const selectedCategoryIdx = useSelector(
    (state) => state.menu.selectedCategoryIdx
  );
  const headerRef = React.useRef(null);
  const tabsRef = React.useRef(null);

  const searchItem = (query) => dispatch(updateSearch(query));

  const [search, setSearch] = useState('');
  const [isUserClick, setIsUserClick] = React.useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [headerHeight, setHeaderHeight] = React.useState(180);
  const setCategory = (idx) => dispatch(selectedCategory(idx));
  const order = useSelector((state) => state.order);

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  // Custom Intersection Observer for scroll detection
  useEffect(() => {
    if (!categories || categories.length === 0) return;

    const observerOptions = {
      root: null,
      rootMargin: `-${headerHeight}px 0px -50% 0px`, // Account for fixed header
      threshold: [0, 0.25, 0.5, 0.75, 1]
    };

    const observerCallback = (entries) => {
      if (isUserClick) return; // Don't update if user just clicked a tab
      
      // Check if we're restoring scroll position (within 2 seconds of restore time)
      try {
        const restoreTime = sessionStorage.getItem('menuScrollRestoreTime');
        if (restoreTime) {
          const timeSinceRestore = Date.now() - parseInt(restoreTime, 10);
          if (timeSinceRestore < 2000) {
            return; // Don't update during scroll restoration
          }
        }
      } catch (e) {
        // Safari private mode can throw errors on sessionStorage access
        console.warn('Failed to read scroll restore time:', e);
      }

      // Find the section that's most visible
      let maxRatio = 0;
      let visibleIndex = -1;

      entries.forEach((entry) => {
        if (entry.isIntersecting && entry.intersectionRatio > maxRatio) {
          maxRatio = entry.intersectionRatio;
          const categoryId = entry.target.id;
          const index = categories.findIndex(cat => cat.id === categoryId);
          if (index !== -1) {
            visibleIndex = index;
          }
        }
      });

      if (visibleIndex !== -1 && visibleIndex !== selectedCategoryIdx) {
        setCategory(visibleIndex);
        
        // Auto-scroll the tab into view
        setTimeout(() => {
          const tabElement = document.querySelector(`#scrollable-auto-tab-${visibleIndex}`);
          if (tabElement) {
            tabElement.scrollIntoView({
              behavior: 'smooth',
              block: 'nearest',
              inline: 'center'
            });
          }
        }, 50);
      }
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Observe all category sections
    categories.forEach((category) => {
      const element = document.getElementById(category.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [categories, selectedCategoryIdx, isUserClick, setCategory, headerHeight]);

  // Reset user click flag after a delay
  useEffect(() => {
    if (isUserClick) {
      const timer = setTimeout(() => {
        setIsUserClick(false);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [isUserClick]);

  const cartLinkTarget = `/cart/${menuId}/${tableId}`;

  const chooseCategory = (e, onClick, index) => {
    e.preventDefault();
    setIsUserClick(true); // User is clicking, disable scroll detection temporarily
    setCategory(index);
    
    // Clear search to show the full category first
    if (search.length > 0) {
      clearSearch();
    }
    
    // Execute the scroll action after state updates
    if (onClick) {
      // Use requestAnimationFrame to ensure DOM updates complete before scrolling
      requestAnimationFrame(() => {
        onClick();
      });
    }
  };


  const getImgNameFromType = () => {
    const type = getOrderType();
    if (type.startsWith("Table")) {
      return "table";
    } else {
      return type;
    }
  };

  const getOrderType = () => {
    let orderType = tableIdDisplay;
    if (!orderType && decodeRestaurantId(menuId)) {
      orderType = loadTableId(decodeRestaurantId(menuId));
    }
    if (isDineInTable(orderType)) {
      return `Table-${orderType && orderType.split(";")[0]}`;
    } else {
      return orderType;
    }
  };

  const cartItemCount = order.cart.reduce((count, curItem) => {
    return count + curItem.quantity;
  }, 0);

  const searchUpdate = (event) => {
    setSearch(event.target.value);
    searchItem(event.target.value);
  };

  const clearSearch = () => {
    setSearch('');
    searchItem('');
  }

  const handleMouseDown = event => {
    event.preventDefault();
  };

  const endAdornment = () => {
    if (search) {
      return (
        <InputAdornment position="end">
          <IconButton
            onClick={clearSearch}
            onMouseDown={handleMouseDown}
            color="secondary"
          >
            <ClearIcon />
          </IconButton>
        </InputAdornment>
      );
    }

    return "";
  };

  // Calculate header height for spacer
  React.useEffect(() => {
    if (headerRef.current) {
      const updateHeight = () => {
        const height = headerRef.current?.parentElement?.offsetHeight || 180;
        setHeaderHeight(height);
      };
      updateHeight();
      window.addEventListener('resize', updateHeight);
      return () => window.removeEventListener('resize', updateHeight);
    }
  }, [categories]);

  return (
    <Fragment>
      {/* Fixed header */}
      <Box sx={styles.fixedWrapper}>
        <AppBar ref={headerRef} sx={styles.appBar} id="header">
        <Toolbar sx={styles.toolbar}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            {ordersSummary && ordersSummary.length > 0 ? (
              <IconButton
                edge="start"
                aria-label="menu"
                onClick={toggleDrawer(true)}
                sx={styles.menuButton}
              >
                <MenuIcon />
              </IconButton>
            ) : null}
            
            <RestaurantIcon sx={styles.tableIcon} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
              {restaurantName && (
                <Typography sx={styles.restaurantName}>
                  {restaurantName}
                </Typography>
              )}
              {tableId !== 'onlineMenu' && (
                <Typography sx={styles.orderType}>
                  {getOrderType().startsWith("Table")
                    ? `Table ${getOrderType().split("-")[1]}`
                    : getOrderType()}
                </Typography>
              )}
            </Box>
            
            {tableId !== 'onlineMenu' && (
              <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                <CallButton tableId={tableIdDisplay} />
                <NavLink to={cartLinkTarget}>
                  <IconButton
                    aria-label="show cart item number"
                    color="inherit"
                    sx={styles.cartButton}
                  >
                    <Badge
                      badgeContent={cartItemCount.toString()}
                      color="secondary"
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.75rem',
                          fontWeight: 600,
                        }
                      }}
                    >
                      <ShoppingCartIcon sx={styles.cartIcon} />
                    </Badge>
                  </IconButton>
                </NavLink>
              </Box>
            )}
          </Box>
        </Toolbar>
        
        <Box sx={styles.searchContainer}>
          <Box sx={styles.searchIcon}>
            <SearchIcon />
          </Box>
          <InputBase
            value={search}
            placeholder="Search menu items..."
            onChange={searchUpdate}
            endAdornment={endAdornment()}
            sx={styles.searchInput}
            inputProps={{ 'aria-label': 'search' }}
          />
        </Box>
        
        <Paper sx={styles.tabsContainer}>
            <Tabs
              action={tabsRef}
              value={selectedCategoryIdx}
              variant="scrollable"
              indicatorColor="primary"
              scrollButtons="auto"
              aria-label="scrollable auto tabs category"
              sx={{
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                }
              }}
            >
              {sections && sections.length > 0 ? (
                sections.map(({ id, onClick, selected }, index) => {
                  return (
                    <Tab
                      key={id}
                      onClick={(e) => chooseCategory(e, onClick, index)}
                      label={
                        <Box sx={{ textAlign: 'center', py: 0.5 }}>
                          <Typography 
                            sx={{ 
                              fontSize: { xs: '0.75rem', sm: '0.85rem' },
                              fontWeight: 600,
                              lineHeight: 1.2
                            }}
                          >
                            {categories[index]?.name || ''}
                          </Typography>
                          {categories[index]?.altName && (
                            <Typography 
                              sx={{ 
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                color: 'text.secondary',
                                lineHeight: 1.2,
                                marginTop: 0.25
                              }}
                            >
                              {categories[index].altName}
                            </Typography>
                          )}
                        </Box>
                      }
                      sx={styles.tab}
                      {...a11yProps(index)}
                    />
                  );
                })
              ) : (
                categories.map((category, index) => {
                  return (
                    <Tab
                      key={category.id}
                      onClick={(e) => {
                        e.preventDefault();
                        setIsUserClick(true); // User is clicking, disable scroll detection temporarily
                        setCategory(index);
                        if (search.length > 0) {
                          clearSearch();
                        }
                        // Scroll to the category section manually
                        const sectionElement = document.getElementById(category.id);
                        if (sectionElement) {
                          const headerHeight = 180; // Approximate header height
                          const elementPosition = sectionElement.getBoundingClientRect().top;
                          const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
                          
                          window.scrollTo({
                            top: offsetPosition,
                            behavior: 'smooth'
                          });
                        }
                      }}
                      label={
                        <Box sx={{ textAlign: 'center', py: 0.5 }}>
                          <Typography 
                            sx={{ 
                              fontSize: { xs: '0.75rem', sm: '0.85rem' },
                              fontWeight: 600,
                              lineHeight: 1.2
                            }}
                          >
                            {category.name}
                          </Typography>
                          {category.altName && (
                            <Typography 
                              sx={{ 
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                color: 'text.secondary',
                                lineHeight: 1.2,
                                marginTop: 0.25
                              }}
                            >
                              {category.altName}
                            </Typography>
                          )}
                        </Box>
                      }
                      sx={styles.tab}
                      {...a11yProps(index)}
                    />
                  );
                })
              )}
            </Tabs>
          </Paper>
        </AppBar>
      </Box>

      {/* Spacer to push content below fixed header */}
      <Box sx={{ ...styles.spacer, height: headerHeight }} />

      {/* Hamburger Menu Drawer */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={toggleDrawer(false)}
      >
        <Box
          sx={styles.drawer}
          role="presentation"
          onClick={toggleDrawer(false)}
          onKeyDown={toggleDrawer(false)}
        >
          <Box sx={styles.drawerHeader}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Menu
            </Typography>
          </Box>
          <Divider />
          <List>
            {ordersSummary && ordersSummary.length > 0 && (
              <NavLink
                to={`/orderSuccessDetail/${menuId}/${tableId}/abc}`}
                style={styles.listItemLink}
              >
                <ListItem button>
                  <ListItemIcon>
                    <ReceiptIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText 
                    primary="My Orders" 
                    primaryTypographyProps={{
                      fontWeight: 600,
                      fontSize: '0.95rem'
                    }}
                  />
                </ListItem>
              </NavLink>
            )}
          </List>
        </Box>
      </Drawer>
    </Fragment>
  );
};

export default DynamicCategories;
